// frontend/app/components/lessonPlans/authoring/WizardStepReview.js
//
// Step 4: what is wrong, what will ship, and publish.
//
// Two different checks land here and they are not interchangeable:
//   - draft validation, which runs locally and catches blanks
//   - asset resolution, which only the server can do, and which is the one
//     that decides whether a 3D scene actually opens in a classroom

import React from "react";
import { ActivityIndicator, StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import { ChipSelect, Field, SectionCard } from "./WizardFields";
import { isWeightedEvidence } from "./blockSchemas";
import { loopWarnings } from "./loopValidation";
import { SECTION_META, getSectionMeta, getStepSection } from "../lessonPlanTheme";

const STATUS_OPTIONS = [
  { value: "draft", label: "Draft — only teachers see it", accent: "#f59e0b" },
  { value: "published", label: "Published — live for this topic", accent: "#10b981" },
];

/** See LessonResultRow.fill: a translator gets one whole clause, not fragments. */
const fill = (template, values) =>
  String(template).replace(/\{(\w+)\}/g, (token, key) =>
    Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : token
  );

// `GET .../runtime`'s `validation.shape` runs the same authoring gate
// (`services/lesson_shape.py`) that `Scripts/verify_lessons.py` runs against
// file-authored lessons. An out-of-order lesson is a LOOP_SECTION_ORDER finding whose `sections` are the
// keys in the order the steps run. A missing section is reported once, as a
// LOOP_SECTION_MISSING finding whose `sections` list names the section keys;
// each gets its own line here. Older payloads without `sections` fall back to
// the finding's message.

/** `validation.shape` findings, expanded into one display row per missing
 * section and one per other finding. */
const shapeFindingItems = (shapeFindings, translate) =>
  (shapeFindings || []).flatMap((finding, index) => {
    if (finding.code === "LOOP_SECTION_MISSING" && Array.isArray(finding.sections)) {
      return finding.sections.map((sectionKey) => ({
        key: `shape-missing-${sectionKey}`,
        level: finding.level,
        text: fill(translate("No {section} step."), {
          section: translate(getSectionMeta(sectionKey).label),
        }),
      }));
    }
    if (finding.code === "LOOP_SECTION_ORDER" && Array.isArray(finding.sections)) {
      return [
        {
          key: "shape-order",
          level: finding.level,
          text: fill(translate("Steps are out of order: {sections}."), {
            sections: finding.sections
              .map((sectionKey) => translate(getSectionMeta(sectionKey).label))
              .join(" → "),
          }),
        },
      ];
    }
    return [
      {
        key: `shape-${finding.code}-${index}`,
        level: finding.level,
        text: translate(finding.message),
      },
    ];
  });

/** The four sections, in order — "1. Introduction" … "4. Summary" — each with
 * how many steps it holds. A section the lesson skips is still listed; the
 * checks below are what say it is missing. */
const sectionStripEntries = (steps) =>
  Object.entries(SECTION_META).map(([key, meta]) => ({
    key,
    label: meta.label,
    accent: meta.accent,
    count: (steps || []).filter((step) => getStepSection(step) === key).length,
  }));

const ProblemList = ({ colors, title, icon, accent, items, emptyLabel, t }) => {
  const translate = t || ((key) => key);
  return (
    <View style={styles.problemBlock}>
      <View style={styles.problemHeader}>
        <MaterialCommunityIcons name={icon} size={15} color={accent} />
        <Text style={[styles.problemTitle, { color: colors.textPrimary }]}>
          {translate(title)}
        </Text>
        <View
          style={[
            styles.countPill,
            { backgroundColor: items.length ? `${accent}22` : colors.subtleBg },
          ]}
        >
          <Text
            style={[
              styles.countText,
              { color: items.length ? accent : colors.textMuted },
            ]}
          >
            {items.length}
          </Text>
        </View>
      </View>

      {items.length === 0 ? (
        <Text style={[styles.emptyLine, { color: colors.textMuted }]}>
          {translate(emptyLabel)}
        </Text>
      ) : (
        items.map((item, index) => (
          <View
            key={`${item.key || index}`}
            style={[styles.problemRow, { borderColor: colors.border }]}
          >
            <Text style={[styles.problemWhere, { color: colors.textMuted }]}>
              {item.where}
            </Text>
            <Text style={[styles.problemText, { color: colors.textPrimary }]}>
              {item.text}
            </Text>
          </View>
        ))
      )}
    </View>
  );
};

const WizardStepReview = ({
  colors,
  draft,
  concepts = [],
  steps = [],
  validation,
  unresolved = null,
  shapeFindings = null,
  checkingAssets = false,
  onCheckAssets,
  onChangeStatus,
  saveError,
  t,
}) => {
  const translate = t || ((key) => key);
  const shapeItems = shapeFindingItems(shapeFindings, translate);

  const fieldProblems = [
    ...Object.entries(validation.details || {}).map(([key, text]) => ({
      key: `d-${key}`,
      where: translate("Details"),
      text: translate(text),
    })),
    ...Object.entries(validation.concepts || {}).map(([key, text]) => ({
      key: `c-${key}`,
      where: translate("Concepts"),
      text: translate(text),
    })),
    ...Object.entries(validation.phases || {}).map(([key, text]) => ({
      key: `p-${key}`,
      where: translate("Lesson flow"),
      text: translate(text),
    })),
  ];

  const blockProblems = (validation.blockProblems || []).map((problem, index) => ({
    key: `b-${index}`,
    where: `${problem.step_title || problem.step_key} · ${problem.block_type}`,
    text: translate(problem.problem),
  }));

  const assetProblems = (unresolved || []).map((item, index) => ({
    key: `a-${index}`,
    where: `${item.step_key} · ${item.type}`,
    text:
      (item.error ? translate(item.error) : null) ||
      `${translate("Could not be found in the curriculum")} (${item.status})`,
  }));

  const checkCount = steps.reduce(
    (sum, step) =>
      sum + (step.blocks || []).filter((b) => b.concept_key).length,
    0
  );
  // Only these move a band. A lesson can be full of tagged predictions and
  // still have nothing Insights can measure.
  const scoredCount = steps.reduce(
    (sum, step) =>
      sum +
      (step.blocks || []).filter((b) => b.concept_key && isWeightedEvidence(b.type))
        .length,
    0
  );

  const loopProblems = loopWarnings({ concepts, steps, t: translate });

  return (
    <View>
      <SectionCard
        colors={colors}
        title={translate("What will ship")}
        icon="clipboard-text-outline"
      >
        <Text style={[styles.summaryTitle, { color: colors.textPrimary }]}>
          {draft.title || translate("Untitled lesson")}
        </Text>
        <Text style={[styles.summaryPath, { color: colors.textMuted }]}>
          {[draft.subject_name, draft.chapter_name, draft.topic_name]
            .filter(Boolean)
            .join("  ›  ") || translate("No topic selected")}
        </Text>

        <View style={styles.statRow}>
          {[
            { label: translate("steps"), value: steps.length },
            { label: translate("objectives"), value: (draft.objectives || []).filter((o) => String(o).trim()).length },
            { label: translate("concepts"), value: concepts.length },
            { label: translate("tagged checks"), value: checkCount },
            { label: translate("scored"), value: scoredCount },
          ].map((stat) => (
            <View key={stat.label} style={[styles.stat, { borderColor: colors.border }]}>
              <Text style={[styles.statValue, { color: colors.textPrimary }]}>
                {stat.value}
              </Text>
              <Text style={[styles.statLabel, { color: colors.textMuted }]}>
                {stat.label}
              </Text>
            </View>
          ))}
        </View>

        {concepts.length === 0 ? (
          <Text style={[styles.warnLine, { color: colors.warning }]}>
            {translate("No concepts declared — this lesson will have no Insights.")}
          </Text>
        ) : checkCount === 0 ? (
          <Text style={[styles.warnLine, { color: colors.warning }]}>
            {translate("No check is tagged to a concept — Insights will stay empty.")}
          </Text>
        ) : null}

        <View style={styles.phaseStrip}>
          {sectionStripEntries(steps).map((entry, index) => (
            <View
              key={entry.key}
              style={[styles.phasePill, { backgroundColor: `${entry.accent}18` }]}
            >
              <Text style={[styles.phasePillText, { color: entry.accent }]}>
                {index + 1}. {translate(entry.label)}
                {entry.count > 1 ? ` (${entry.count})` : ""}
              </Text>
            </View>
          ))}
        </View>
      </SectionCard>

      <SectionCard
        colors={colors}
        title={translate("Checks")}
        subtitle={translate("Everything below must be clear before publishing.")}
        icon="check-decagram-outline"
      >
        <ProblemList
          colors={colors}
          title="Missing details"
          icon="form-textbox"
          accent={colors.danger}
          items={fieldProblems}
          emptyLabel="Nothing missing."
          t={t}
        />
        <ProblemList
          colors={colors}
          title="Content problems"
          icon="cube-outline"
          accent={colors.warning}
          items={blockProblems}
          emptyLabel="Every block is complete."
          t={t}
        />
        {/* Advisory: a four-step lesson is a legitimate lesson. What is not
            legitimate is publishing one that can never produce a score and
            only finding out when Insights comes back empty. */}
        <ProblemList
          colors={colors}
          title="Learning Loop"
          icon="autorenew"
          accent={colors.info}
          items={loopProblems}
          emptyLabel="A complete Loop, and every concept is measurable."
          t={t}
        />

        <View style={styles.problemBlock}>
          <View style={styles.problemHeader}>
            <MaterialCommunityIcons name="link-variant" size={15} color={colors.info} />
            <Text style={[styles.problemTitle, { color: colors.textPrimary }]}>
              {translate("Saras content")}
            </Text>
            <View style={{ flex: 1 }} />
            <TouchableOpacity
              onPress={onCheckAssets}
              disabled={checkingAssets}
              accessibilityRole="button"
              accessibilityLabel={translate("Check content against the curriculum")}
              style={[styles.checkButton, { borderColor: colors.info }]}
            >
              {checkingAssets ? (
                <ActivityIndicator size="small" color={colors.info} />
              ) : (
                <MaterialCommunityIcons name="refresh" size={13} color={colors.info} />
              )}
              <Text style={[styles.checkButtonText, { color: colors.info }]}>
                {translate("Check now")}
              </Text>
            </TouchableOpacity>
          </View>

          {unresolved === null ? (
            <Text style={[styles.emptyLine, { color: colors.textMuted }]}>
              {translate(
                "Not checked yet. Only the server can tell whether a scene or video actually exists."
              )}
            </Text>
          ) : assetProblems.length === 0 ? (
            <Text style={[styles.emptyLine, { color: colors.success }]}>
              {translate("Every scene, video and quiz resolved.")}
            </Text>
          ) : (
            assetProblems.map((item) => (
              <View
                key={item.key}
                style={[styles.problemRow, { borderColor: colors.border }]}
              >
                <Text style={[styles.problemWhere, { color: colors.textMuted }]}>
                  {item.where}
                </Text>
                <Text style={[styles.problemText, { color: colors.textPrimary }]}>
                  {item.text}
                </Text>
              </View>
            ))
          )}
        </View>

        {/* The same authoring gate `Scripts/verify_lessons.py` runs for
            file-authored lessons, run here against this wizard-authored one.
            Advisory only, like Learning Loop above it — this never blocks
            saving or publishing. */}
        <View style={styles.problemBlock}>
          <View style={styles.problemHeader}>
            <MaterialCommunityIcons name="shield-alert-outline" size={15} color={colors.info} />
            <Text style={[styles.problemTitle, { color: colors.textPrimary }]}>
              {translate("Lesson shape")}
            </Text>
            <View
              style={[
                styles.countPill,
                { backgroundColor: shapeItems.length ? `${colors.warning}22` : colors.subtleBg },
              ]}
            >
              <Text
                style={[
                  styles.countText,
                  { color: shapeItems.length ? colors.warning : colors.textMuted },
                ]}
              >
                {shapeItems.length}
              </Text>
            </View>
          </View>

          {shapeFindings === null ? (
            <Text style={[styles.emptyLine, { color: colors.textMuted }]}>
              {translate("Not checked yet. Check content above to see this lesson's shape.")}
            </Text>
          ) : shapeItems.length === 0 ? (
            <Text style={[styles.emptyLine, { color: colors.success }]}>
              {translate("This lesson covers all four sections, and no concept is still a placeholder.")}
            </Text>
          ) : (
            shapeItems.map((item) => (
              <View
                key={item.key}
                style={[styles.problemRow, { borderColor: colors.border }]}
              >
                <Text
                  style={[
                    styles.problemText,
                    { color: item.level === "error" ? colors.danger : colors.warning },
                  ]}
                >
                  {item.text}
                </Text>
              </View>
            ))
          )}
        </View>
      </SectionCard>

      <SectionCard colors={colors} title={translate("Visibility")} icon="eye-outline">
        <Field colors={colors}>
          <ChipSelect
            colors={colors}
            options={STATUS_OPTIONS.map((o) => ({ ...o, label: translate(o.label) }))}
            value={draft.status}
            onChange={onChangeStatus}
            accessibilityLabel={translate("Visibility")}
          />
        </Field>

        {draft.status === "published" && validation.blocking ? (
          <Text style={[styles.warnLine, { color: colors.danger }]}>
            {translate("Fix the problems above before publishing.")}
          </Text>
        ) : null}

        {/* Unlike the Loop warnings, this one blocks: a scene that does not
            resolve is a lesson that dead-ends in a classroom. */}
        {draft.status === "published" && assetProblems.length ? (
          <Text style={[styles.warnLine, { color: colors.danger }]}>
            {translate(
              "Saras content that does not resolve cannot be published. Fix it above, or leave this lesson as a draft."
            )}
          </Text>
        ) : null}

        {saveError ? (
          <View
            style={[
              styles.saveError,
              { backgroundColor: colors.incorrectBg, borderColor: colors.incorrectBorder },
            ]}
          >
            <Text style={[styles.saveErrorText, { color: colors.textPrimary }]}>
              {saveError}
            </Text>
          </View>
        ) : null}
      </SectionCard>
    </View>
  );
};

const styles = StyleSheet.create({
  summaryTitle: { fontSize: 18, fontWeight: "800" },
  summaryPath: { fontSize: 12, marginTop: 3 },
  statRow: { flexDirection: "row", flexWrap: "wrap", gap: 8, marginTop: 14 },
  stat: {
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 8,
    minWidth: 78,
  },
  statValue: { fontSize: 17, fontWeight: "800" },
  statLabel: { fontSize: 10, marginTop: 1 },
  warnLine: { fontSize: 12, fontWeight: "600", marginTop: 12 },

  phaseStrip: { flexDirection: "row", flexWrap: "wrap", gap: 6, marginTop: 14 },
  phasePill: { borderRadius: 999, paddingHorizontal: 10, paddingVertical: 5 },
  phasePillText: { fontSize: 11, fontWeight: "700" },

  problemBlock: { marginBottom: 16 },
  problemHeader: { flexDirection: "row", alignItems: "center", gap: 7, marginBottom: 8 },
  problemTitle: { fontSize: 13, fontWeight: "700" },
  countPill: {
    minWidth: 22,
    borderRadius: 999,
    paddingHorizontal: 7,
    paddingVertical: 2,
    alignItems: "center",
  },
  countText: { fontSize: 11, fontWeight: "800" },
  problemRow: { borderWidth: 1, borderRadius: 9, padding: 9, marginBottom: 6 },
  problemWhere: {
    fontSize: 10,
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  problemText: { fontSize: 13, marginTop: 2 },
  emptyLine: { fontSize: 12, fontStyle: "italic" },

  checkButton: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 999,
    borderWidth: 1,
    paddingHorizontal: 10,
    paddingVertical: 4,
    gap: 5,
  },
  checkButtonText: { fontSize: 11, fontWeight: "700" },

  saveError: { borderRadius: 10, borderWidth: 1, padding: 12, marginTop: 12 },
  saveErrorText: { fontSize: 13 },
});

export default WizardStepReview;
